import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { Button, Layout, Menu, Spin, Typography } from 'antd';
import { AppstoreOutlined, BookOutlined, DollarOutlined, ExperimentOutlined, HomeOutlined, LogoutOutlined, QuestionCircleOutlined, ReadOutlined, SettingOutlined, TeamOutlined, } from '@ant-design/icons';
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import LoginScreen from './screens/LoginScreen';
import DashboardScreen from './screens/DashboardScreen';
import GradesListScreen from './screens/grades/GradesListScreen';
import GradePageScreen from './screens/grades/GradePageScreen';
import TrackPageScreen from './screens/grades/TrackPageScreen';
import LessonPageScreen from './screens/grades/LessonPageScreen';
import TemplatesListScreen from './screens/templates/TemplatesListScreen';
import TemplatePageScreen from './screens/templates/TemplatePageScreen';
import AssessmentsListScreen from './screens/assessments/AssessmentsListScreen';
import AssessmentPageScreen from './screens/assessments/AssessmentPageScreen';
import CouponsScreen from './screens/CouponsScreen';
import UpsellRulesScreen from './screens/UpsellRulesScreen';
import ConfigScreen from './screens/ConfigScreen';
import UsersScreen from './screens/UsersScreen';
import StudentPageScreen from './screens/StudentPageScreen';
import GuideScreen from './screens/GuideScreen';
import navBg from './assets/nav-bg.png';
const { Header, Content, Sider } = Layout;
const { Title, Text } = Typography;
// --- Admin facelift tokens — pulled from the mobile app, do not invent.
// APP_PURPLE: `palette.primary` in apps/mobile/src/theme/colors.ts:58
// (primary CTA + banner text + active tab). White text on it = 4.77:1 contrast,
// which passes WCAG AA for normal text.
// NAV_BG: apps/mobile/assets/settings/page-bg.png (SettingsScreen bg),
// copied to apps/admin/src/assets/nav-bg.png. Anchored at the Sider's
// bottom; the top of the panel is white and fades softly into the image
// so the nav labels sit on the solid upper portion.
const APP_PURPLE = '#8B2FD6';
const NAV = [
    { key: '/', label: 'בית', icon: _jsx(HomeOutlined, {}) },
    { key: '/grades', label: 'כיתות', icon: _jsx(AppstoreOutlined, {}) },
    { key: '/templates', label: 'תבניות שאלה', icon: _jsx(QuestionCircleOutlined, {}) },
    { key: '/assessments', label: 'בחנים', icon: _jsx(ExperimentOutlined, {}) },
    { key: '/coupons', label: 'קופונים', icon: _jsx(DollarOutlined, {}) },
    { key: '/upsell-rules', label: 'כללי Upsell', icon: _jsx(DollarOutlined, {}) },
    { key: '/users', label: 'תלמידים', icon: _jsx(TeamOutlined, {}) },
    { key: '/guide', label: 'מדריכים', icon: _jsx(BookOutlined, {}), newTab: true },
    { key: '/config', label: 'הגדרות', icon: _jsx(SettingOutlined, {}) },
];
export default function App() {
    const { status, email, logout } = useAuth();
    const location = useLocation();
    const nav = useNavigate();
    if (status === 'checking') {
        return _jsx("div", { style: { display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }, children: _jsx(Spin, {}) });
    }
    if (status === 'anon')
        return _jsx(LoginScreen, {});
    // /guide renders OUTSIDE the admin Header/Sider layout: the iframe
    // fills the whole tab. Still auth-gated — we already returned the
    // login screen above for anon.
    if (location.pathname === '/guide')
        return _jsx(GuideScreen, {});
    // Sidebar highlighting: match by the FIRST url segment so deep links like
    // /grades/1/tracks/2 still highlight "כיתות". On the home page ("/")
    // there's no segment → activeKey = "/", which matches the Dashboard
    // nav item.
    const firstSeg = location.pathname.split('/').filter(Boolean)[0];
    const activeKey = firstSeg ? `/${firstSeg}` : '/';
    return (_jsxs(Layout, { style: { minHeight: '100vh' }, children: [_jsxs(Header, { style: {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    padding: '0 24px',
                    background: '#ffffff',
                    borderBottom: '1px solid rgba(107, 33, 168, 0.06)',
                    boxShadow: '0 2px 8px rgba(34, 23, 75, 0.06), 0 8px 24px rgba(34, 23, 75, 0.04)',
                    // Pin the header to the viewport top. z-index above the
                    // sticky Sider (default 0) so the header always paints over
                    // the sidebar's top edge, and well above page content.
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                }, children: [_jsx("span", { "aria-hidden": true, style: {
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 36,
                            height: 36,
                            borderRadius: 10,
                            background: '#EFE9FA',
                            color: APP_PURPLE,
                            fontSize: 18,
                        }, children: _jsx(ReadOutlined, {}) }), _jsx(Title, { level: 4, style: { color: '#22174B', margin: 0, fontWeight: 700 }, children: "\u05DE\u05EA\u05DE\u05D8\u05D9\u05E7\u05DC\u05D9 \u00B7 \u05E4\u05D0\u05E0\u05DC \u05E0\u05D9\u05D4\u05D5\u05DC" }), _jsx("div", { style: { flex: 1 } }), _jsx(Text, { style: { color: '#6D6386' }, children: email }), _jsx(Button, { icon: _jsx(LogoutOutlined, {}), onClick: logout, style: { color: '#22174B', borderColor: 'rgba(34, 23, 75, 0.15)' }, children: "\u05D4\u05EA\u05E0\u05EA\u05E7" })] }), _jsxs(Layout, { children: [_jsx(Sider, { width: 250, theme: "light", style: {
                            borderInlineStart: '1px solid #f0f0f0',
                            paddingTop: 10,
                            // Pin the sidebar: sticks just below the 64px AntD header
                            // while the main content scrolls underneath. Capped at
                            // `100vh - 64px` so it never overlaps the header, with its
                            // own internal scroll if the nav ever outgrows that cap.
                            // Default `background-attachment: scroll` keeps the layered
                            // gradient + bottom-anchored nav image glued to the Sider's
                            // own box, not to its scrolled content, so the bottom-
                            // anchored image stays at the visible bottom.
                            position: 'sticky',
                            top: 64,
                            height: 'calc(100vh - 64px)',
                            overflowY: 'auto',
                            // Live-tuned stack (DevTools): solid white top-half, fades
                            // to transparent by 100%, revealing the nav bg image anchored
                            // at `center bottom`; white base behind it.
                            background: `
              linear-gradient(rgb(255,255,255) 0%, rgb(255,255,255) 50%, rgba(255,255,255,0) 100%),
              url(${navBg}) center bottom / 100% no-repeat,
              rgb(255,255,255)
            `,
                        }, children: _jsx(Menu, { mode: "inline", selectedKeys: [activeKey], style: { height: '100%', borderInlineEnd: 0, background: 'transparent' }, items: NAV.map((n) => {
                                if (n.newTab) {
                                    // Opens the guide in a new tab. We own the whole click
                                    // via onClick so middle-click vs left-click behave the
                                    // same; the label is a plain span (no nested <a>) to
                                    // avoid double-open from event bubbling.
                                    return {
                                        key: n.key,
                                        icon: n.icon,
                                        label: _jsx("span", { children: n.label }),
                                        onClick: () => window.open(n.key, '_blank', 'noopener,noreferrer'),
                                    };
                                }
                                return {
                                    key: n.key,
                                    icon: n.icon,
                                    label: _jsx(Link, { to: n.key, children: n.label }),
                                    onClick: () => nav(n.key),
                                };
                            }) }) }), _jsx(Content, { style: { padding: 24, background: '#F1EAFF', overflow: 'auto' }, children: _jsxs(Routes, { children: [_jsx(Route, { path: "/", element: _jsx(DashboardScreen, {}) }), _jsx(Route, { path: "/grades", element: _jsx(GradesListScreen, {}) }), _jsx(Route, { path: "/grades/:gradeId", element: _jsx(GradePageScreen, {}) }), _jsx(Route, { path: "/grades/:gradeId/tracks/:trackId", element: _jsx(TrackPageScreen, {}) }), _jsx(Route, { path: "/grades/:gradeId/tracks/:trackId/lessons/:lessonId", element: _jsx(LessonPageScreen, {}) }), _jsx(Route, { path: "/templates", element: _jsx(TemplatesListScreen, {}) }), _jsx(Route, { path: "/templates/:id", element: _jsx(TemplatePageScreen, {}) }), _jsx(Route, { path: "/assessments", element: _jsx(AssessmentsListScreen, {}) }), _jsx(Route, { path: "/assessments/:id", element: _jsx(AssessmentPageScreen, {}) }), _jsx(Route, { path: "/coupons", element: _jsx(CouponsScreen, {}) }), _jsx(Route, { path: "/upsell-rules", element: _jsx(UpsellRulesScreen, {}) }), _jsx(Route, { path: "/users", element: _jsx(UsersScreen, {}) }), _jsx(Route, { path: "/students/:id", element: _jsx(StudentPageScreen, {}) }), _jsx(Route, { path: "/config", element: _jsx(ConfigScreen, {}) }), _jsx(Route, { path: "/content", element: _jsx(Navigate, { to: "/grades", replace: true }) }), _jsx(Route, { path: "/prereqs", element: _jsx(Navigate, { to: "/grades", replace: true }) }), _jsx(Route, { path: "/recommended", element: _jsx(Navigate, { to: "/grades", replace: true }) }), _jsx(Route, { path: "/recommended/:gradeId", element: _jsx(LegacyRecommendedRedirect, {}) }), _jsx(Route, { path: "*", element: _jsx(Navigate, { to: "/grades", replace: true }) })] }) })] })] }));
}
/**
 * /recommended/:gradeId used to open the standalone recommended-path screen;
 * now that view is a tab on the grade page — redirect there so existing
 * bookmarks / deep links keep working.
 */
function LegacyRecommendedRedirect() {
    const { pathname } = useLocation();
    const parts = pathname.split('/').filter(Boolean);
    const id = parts[1];
    return _jsx(Navigate, { to: `/grades/${id}?tab=recommended`, replace: true });
}
